import { jsx as _jsx } from "react/jsx-runtime";
import { getSkeletonClasses } from '#pplx/frontend/aether/components/Skeleton/classes.js';
import { styles } from '#pplx_generated/pplx/frontend/aether/components/Skeleton/Skeleton.module.css.js';
import classNames from 'classnames';
/**
 * Decorative loading placeholder with semantic shape, size, width, and animation variants.
 *
 * @category components
 * @keywords skeleton, shimmer, placeholder, loading, pulse, text, rectangle, circle, avatar
 * @whenToUse Use Skeleton when the layout of content is known before the content itself is ready.
 *   Compose text, rectangle, and circle shapes to approximate the stable structure that will
 *   replace them. Reserve the shimmer treatment for waits users actually watch: a noticeable,
 *   indeterminate wait of roughly a second or more where the user is likely looking at the
 *   region. Shimmer is delight layered on top of a clear loading state, not the primary way to
 *   communicate that work is happening. For an indeterminate operation without a representative
 *   content layout, use Spinner instead.
 * @guidelineDo
 *   - Match each placeholder to the shape and approximate size of the content it replaces
 *   - Use a small set of shapes that preserve the final layout without recreating every detail
 *   - Use the default shimmer treatment for new skeleton layouts
 *   - Use pulse only when matching existing pulse-based placeholders in the same loading region
 *   - Compose as many placeholder lines and shapes as the layout needs within one loading region
 *   - Treat a skeleton that stays visible for a long time as a signal to make the underlying
 *     work faster, not as a reason to add more loading animation
 *   - Set aria-busy on the owning content region and provide a status label when loading needs to
 *     be announced
 * @guidelineDont
 *   - Don't use Skeleton for operations whose final layout is unknown — use Spinner instead
 *   - Don't make decorative placeholders focusable or interactive
 *   - Don't mix shimmer and pulse treatments within one loading region
 *   - Don't rely on shimmer or pulse animation as the only indication that content is loading;
 *     animation-only signals exclude screen reader users and are easy to miss in bright glare or
 *     at very low display brightness
 *   - Don't use a skeleton layout that differs materially from the content that replaces it
 *   - Don't add a skeleton for states that typically resolve in a split second; a brief flash of
 *     animation costs more than it communicates
 *   - Don't run several separate shimmering loading regions on the page at once
 * @accessibility
 *   - Skeleton is always aria-hidden because its shapes are decorative and contain no content
 *   - Put aria-busy='true' on the region whose content is loading
 *   - Add role='status' and visually hidden loading text to the owning region when the state needs
 *     to be announced by screen readers
 *   - Animation stops when the user prefers reduced motion
 * @relatedComponents spinner, progress, shimmer-primitive
 */
export function Skeleton({ animation = 'shimmer', shape = 'text', size = 'md', width = 'full', }) {
    const resolvedWidth = shape === 'circle' ? undefined : width;
    return (_jsx("div", { "aria-hidden": "true", className: classNames(getSkeletonClasses({ animation, shape, size, width: resolvedWidth }), animation === 'shimmer' && styles.shimmer), children: animation === 'shimmer' && (_jsx("span", { className: styles.track, children: _jsx("span", { className: styles.shape }) })) }));
}
//# sourceMappingURL=index.js.map